
import { Options, Vue } from "vue-class-component";
import UserService from "../../service/UserService.js";
import { reactive } from "vue";
import useVuelidate from "@vuelidate/core";
import { helpers, email, required, minLength, numeric } from "@vuelidate/validators";
import Toaster from "../../helpers/Toaster";
import FileUploader from "../../components/FileUploader.vue";
import { useStore, ActionTypes } from "../../store";
import { RoleLevels } from "../../constants/RoleLevels";
import UtilityOptions from "../../mixins/UtilityOptions";

@Options({
    title: "Users",
    components: {
        FileUploader,
    },
})
export default class Users extends UtilityOptions  {
    // Make RoleLevels available in template
    private RoleLevels = RoleLevels;
    
    private lists = [];
    private goToFirstLink = 0;
    private UserService;
    private keyword = "";
    private productDialog = false;
    private uploaderStatus = false;
    private pinDialog = false;
    private editProductDialog = false;
    private submitted = false;
    private statusDialog = false;
    private checkPagination = true;
    private totalRecords = 0;
    private limit = 0;
    private home = { icon: "pi pi-home", to: "/" };

    private items = [
        { label: "Initialization", to: "/home/initialization" },
        { label: "Users", to: "users" },
    ];

    private toast;

    private item = {
        id: 0,
        branchId: 0,
        pharmacyId: 0,
        pharmacyIds: [] as number[], // For Pharmacy Admin multi-select
        role: 1,
        address: "",
        contact: "",
        image: "default.jpg",
        description: "",
        status: "Active",
    };

    private state = reactive({
        name: "",
        email: "",
        password: "",
        confirmPassword: "",
        contactNo: "",
    });

    private stateUpdateUser = reactive({
        name: "",
        email: "",
        contactNo: "",

    });

    validateOneUpperCase(value) {
        if (value == "") {
            return true;
        }
        if (!/[A-Z]/.test(value)) {
            return false;
        }
        return true;
    }
    validateOneLowerCase(value) {
        if (value == "") {
            return true;
        }
        if (!/[a-z]/.test(value)) {
            return false;
        }

        return true;
    }
    validateOneSpecialValue(value) {
        if (value == "") {
            return true;
        }
        if (!/[@$!%*#?&]/.test(value)) {
            return false;
        }
        return true;
    }
    validateOneDigit(value) {
        if (value == "") {
            return true;
        }
        if (!/[0-9]/.test(value)) {
            return false;
        }
        return true;
    }
    matchWithPassword(value) {
        if (value == "") {
            return true;
        }
        if (this.state.password != value) {
            return false;
        }
        return true;
    }

    decimalValidator(value) {
        const decimalPattern = /^\d+$/; // Restrict to max 2 decimal places
        return decimalPattern.test(value);
    }

    validateMobileNo(value) {
        if (!value || value === "") {
            return true;  
        }
        if (!this.validateMobileNumber(value)) {
            return false;
        }
        return true;
    }

    normalizeContact(value: string): string {
        if (!value) return "";
        const formatted = this.formatPhoneNumber(value); 
        return formatted ? formatted.slice(2) : value;  
    }

    private validationRules = {
        name: {
            required,
        },
        email: {
            required,
            email,
        },
        contactNo: {
            numeric: helpers.withMessage(
                "Contact Number should be numeric",
                numeric
            ),
            validateMobileNo: helpers.withMessage(
                "Invalid Contact Number",
                this.validateMobileNo
            ),
        },
        password: {
            minLength: helpers.withMessage(
                "Password must be at least 14 characters long. ",
                minLength(14)
            ),
            validateOneUpperCase: helpers.withMessage(
                "Password must contain at least one uppercase letter. ",
                this.validateOneUpperCase
            ),
            validateOneLowerCase: helpers.withMessage(
                "Password must contain at least one lowercase letter. ",
                this.validateOneLowerCase
            ),
            validateOneDigit: helpers.withMessage(
                "Password must contain at least one number. ",
                this.validateOneDigit
            ),
            validateOneSpecialValue: helpers.withMessage(
                "Password must contain at least one special character. ",
                this.validateOneSpecialValue
            ),
            required,
        },
        confirmPassword: {
            matchWithPassword: helpers.withMessage(
                "Confirm Password must match with Password. ",
                this.matchWithPassword
            ),
            required,
        },
    };

    private editValidationRules = {
        name: {
            required,
        },
        email: {
            required,
            email,
        },
        contactNo: {
            numeric: helpers.withMessage(
                "Contact Number should be numeric",
                (value: string) => !value || /^\d+$/.test(value)
            ),
            validateMobileNo: helpers.withMessage(
                "Invalid Contact Number",
                (value: string) => !value || this.validateMobileNo(value)
            ),
        },
    };

    private pinState = reactive({
        password: "",
        confirmPassword: "",
    });

    private pinValidationRules = {
        password: {
            minLength: helpers.withMessage(
                "Password must be at least 14 characters long. ",
                minLength(14)
            ),
            validateOneUpperCase: helpers.withMessage(
                "Password must contain at least one uppercase letter. ",
                this.validateOneUpperCase
            ),
            validateOneLowerCase: helpers.withMessage(
                "Password must contain at least one lowercase letter. ",
                this.validateOneLowerCase
            ),
            validateOneDigit: helpers.withMessage(
                "Password must contain at least one number. ",
                this.validateOneDigit
            ),
            validateOneSpecialValue: helpers.withMessage(
                "Password must contain at least one special character. ",
                this.validateOneSpecialValue
            ),
            required,
        },
        confirmPassword: {
            matchWithPassword: helpers.withMessage(
                "Confirm Password must match with Password. ",
                (value) => !value || this.pinState.password === value
            ),
            required,
        },
    };

    private vPin$ = useVuelidate(this.pinValidationRules, this.pinState);

    private v$ = useVuelidate(this.validationRules, this.state);
    private v2$ = useVuelidate(this.editValidationRules, this.stateUpdateUser);

    private userRoles;
    // private storesList;
    private pharmacyList;
    private dialogTitle;
    private dialogCallback;
    private beErrors = {};
    private currentUserBranchId = 0;
    private currentUserPharmacyId = 0;

    //CALLING WHEN PAGINATION BUTTON CLICKS
    onPage(event) {
        this.loadList(event.first);
    }

    //DEFAULT METHOD OF TYPE SCRIPT
    //CALLING WHENEVER COMPONENT LOADS
    created() {
        this.UserService = new UserService();
        this.toast = new Toaster();
    }

    //OPEN DIALOG TO ADD NEW ITEM
    openDialog() {
        this.clearItem();

        this.editProductDialog = false;
        this.submitted = false;
        this.dialogTitle = "Add New User";
        this.productDialog = true;
    }

    //CLOSE THE ITEM DAILOG BOX
    hideDialog() {
        this.productDialog = false;
        this.editProductDialog = false;
        this.submitted = false;
    }

    //ADD OR UPDATE THE ITEM VIA HTTP
    saveItem(isFormValid) {
        this.submitted = true;
        if(this.item.pharmacyId == 0){
            // since the Admin doesnt have a pharmacy, we set it to 1 by default
            this.item.pharmacyId = 1;
            this.item.branchId = this.storesList[0] ? this.storesList[0].id:0;
        }else if(this.item.branchId == 0) {
            // since the Pharmacy Admin/Manager doesnt have a branch, we set it to the first branch of the pharmacy
            this.item.branchId = this.storesList[0] ? this.storesList[0].id:0;
        }

        if (isFormValid) {
            if (this.item.id != 0) {
                this.item.contact = this.normalizeContact(this.stateUpdateUser.contactNo);
                this.UserService.updateItem(
                    this.item,
                    this.stateUpdateUser.name,
                    this.stateUpdateUser.email
                ).then((res) => {
                    if (res.status_id == 1) {
                        this.keyword = "";
                        this.goToFirstLink = 0;
                        this.loadList(this.goToFirstLink);
                        //SHOW NOTIFICATION
                        this.toast.handleResponse(res);
                        this.productDialog = false;
                        this.editProductDialog = false;
                        this.clearItem();
                    } else if (res.errors) {
                        this.beErrors = res.errors;
                        //SHOW NOTIFICATION for validation errors - display all error messages
                        const errorMessages = Object.values(res.errors).flat().join('\n');
                        this.toast.showError(errorMessages || res.msg || 'Validation failed. Please check the form.');
                    } else {
                        //SHOW NOTIFICATION
                        this.toast.handleResponse(res);
                        this.productDialog = false;
                        this.editProductDialog = false;
                        this.clearItem();
                    }
                });
            } else {
                if (this.state.password == this.state.confirmPassword) {
                    this.item.contact = this.normalizeContact(this.state.contactNo);
                    this.UserService.saveItem(
                        this.item,
                        this.state.name,
                        this.state.email,
                        this.state.password
                    ).then((res) => {
                        if (res.status_id == 1) {
                            this.keyword = "";
                            this.goToFirstLink = 0;
                            this.loadList(this.goToFirstLink);
                            //SHOW NOTIFICATION
                            this.toast.handleResponse(res);
                            this.productDialog = false;
                            this.editProductDialog = false;
                            this.clearItem();
                        } else if (res.errors) {
                            this.beErrors = res.errors;
                            //SHOW NOTIFICATION for validation errors - display all error messages
                            const errorMessages = Object.values(res.errors).flat().join('\n');
                            this.toast.showError(errorMessages || res.msg || 'Validation failed. Please check the form.');
                        } else {
                            //SHOW NOTIFICATION
                            this.toast.handleResponse(res);
                            this.productDialog = false;
                            this.editProductDialog = false;
                            this.clearItem();
                        }
                    });
                } else {
                    this.toast.showError(
                        "Password & Confirm Password is not matching"
                    );
                }
            }
        }
    }

    clearErrors(key_param) {
        this.beErrors = {};
    }

    changeStorePin(isFormValid) {
        this.submitted = true;
        if (isFormValid) {
            if (this.pinState.password === this.pinState.confirmPassword) {
                this.UserService.changePin(this.item, this.pinState.password).then(
                    (res) => {
                        this.keyword = "";
                        this.loadList(this.goToFirstLink);
                        this.toast.handleResponse(res);
                    }
                );

                this.pinDialog = false;
                this.clearItem();
            } else {
                this.toast.showError(
                    "Password & Confirm Password is not matching"
                );
            }
        }
    }

    //OPEN DIALOG BOX TO EDIT
    editIem(data) {
        this.submitted = false;
        this.dialogTitle = "Update User";
        this.productDialog = false;
        this.editProductDialog = true;

        this.UserService.getItem(data).then((res) => {
            if (res.length > 0) {
                //FIND THE CATEGORY TYPE AND MAKE IT AS SELECTED IN EDIT DIALOG BOX.
                this.item.role = res[0].role;
                // Get pharmacy_id from branch relationship or primary_pharmacy_id
                this.item.pharmacyId = res[0].branch?.pharmacy_id || res[0].primary_pharmacy_id || 0;
                this.item.branchId = res[0].branch_id;
                this.item.address =
                    res[0].address == null ? "" : res[0].address;
                this.item.image = res[0].image;
                this.item.description =
                    res[0].description == null ? "" : res[0].description;
                this.item.status = res[0].status;
                this.item.id = res[0].id;
                this.stateUpdateUser.name = res[0].name;
                this.stateUpdateUser.email = res[0].email;
                this.stateUpdateUser.contactNo = res[0].contact == null ? "" : res[0].contact;

                this.item.role = res[0].role;
                
                // Load assigned pharmacy IDs for Pharmacy Admin
                this.item.pharmacyIds = res[0].assigned_pharmacy_ids || [];

                // this.storesList.filter((elem) => {
                //   if (elem.id == res[0].branch_id) {
                //     this.item.branchId = elem;
                //   }
                // });
            }
        });
    }

    get storesList() {
        return this.pharmacyList.reduce((acc, pharmacy) => {
            // Use loose equality to handle type mismatches (string vs number)
            if (pharmacy.id == this.item.pharmacyId) {
                acc = pharmacy.branches;
            }
            return acc;
        }, []);
    }

    //OPEN DIALOG BOX FOR CONFIRMATION
    confirmChangeStatus(data) {
        this.item.id = data.id;
        this.state.name = data.name;
        this.statusDialog = true;
    }

    changeUserPin(data) {
        this.item.id = data.id;
        this.state.name = data.name;
        this.state.email = data.email;
        this.pinState.password = "";
        this.pinState.confirmPassword = "";
        this.submitted = false;
        this.pinDialog = true;
    }

    //CHANGE THE STATUS AND SEND HTTP TO SERVER
    changeStatus() {
        this.statusDialog = false;
        this.item.status = "Delete";
        this.UserService.changeStatus(this.item).then((res) => {
            this.loadList(0);
            //SHOW NOTIFICATION
            this.toast.handleResponse(res);
        });
    }

    //CALLNING AFTER CONSTRUCTOR GET CALLED
    mounted() {
        this.loadList(0);
        // Add event listener for keypress
        window.addEventListener("keypress", this.handleKeypress);
    }

    beforeUnmount() {
        // Remove event listener
        window.removeEventListener("keypress", this.handleKeypress);
    }

   handleKeypress(event) {
        if (event.key === "Enter") {
            if (this.productDialog || this.editProductDialog || this.pinDialog || this.statusDialog) {
                return;
            }
            this.loadList(this.goToFirstLink);
        }
    }

    pharmacySelectionChange(){
        this.item.branchId = 0;
    };

    //FETCH THE DATA FROM SERVER
    loadList(page) {
        this.UserService.getItems(this.keyword, page).then((data) => {
            // this.storesList = data.branches;
            this.pharmacyList = data.pharmacy_branches;
            this.lists = data.records;
            this.totalRecords = data.totalRecords;
            this.userRoles = data.userRoles;
            this.limit = data.limit;
            this.currentUserBranchId = data.current_user_branch_id;
            this.currentUserPharmacyId = data.current_user_pharmacy_id;
            // Set default pharmacy to current user's pharmacy
            this.item.pharmacyId = this.currentUserPharmacyId || 1;
        });
    }

    clearItem() {
        // Set default role to first available role (based on user's permissions)
        const defaultRole = this.userRoles?.length > 0 ? this.userRoles[0].id : 0;
        
        this.item = {
            id: 0,
            pharmacyId: this.currentUserPharmacyId || 0,
            pharmacyIds: [],
            branchId: this.currentUserBranchId || 0,
            role: defaultRole,
            address: "",
            contact: "",
            image: "default.jpg",
            description: "",
            status: "Active",
        };

        this.stateUpdateUser.name = "";
        this.stateUpdateUser.email = "";
        this.stateUpdateUser.contactNo = "";

        this.state.name = "";
        this.state.email = "";
        this.state.password = "";
        this.state.confirmPassword = "";
        this.state.contactNo = "";
    }

    loadSearchData() {
        this.submitted = true;
        if (this.keyword) {
            this.goToFirstLink = 0;
            this.loadList(0);
        }
    }
    cleanSearchData() {
        this.submitted = true;
        this.keyword = "";
        this.goToFirstLink = 0;
        this.loadList(0);
    }

    openFileUploader(data) {
        this.item.id = data.id;
        this.uploaderStatus = true;
    }

    updateUploaderStatus(params) {
        this.uploaderStatus = false;
        if (params.length > 0) {
            this.UserService.uploadProfileImage(params[0], this.item).then(
                (res) => {
                    this.toast.handleResponse(res);
                }
            );
        }
    }

    getImgUrl(pic: string) {
        // Return the URL path directly - let webpack handle the asset loading
        return "/images/users/" + pic;
    }

    get iamAdmin() {
        const selectedRole = this.userRoles?.find(role => role.id === this.item.role);
        return selectedRole?.level_id === RoleLevels.SUPER_ADMIN;
    }

    get iamPharmacyAdmin() {
        const selectedRole = this.userRoles?.find(role => role.id === this.item.role);
        return selectedRole?.level_id === RoleLevels.INTER_PHARMACY || selectedRole?.name === 'Account Manager';
    }

    get iamManager() {
        const selectedRole = this.userRoles?.find(role => role.id === this.item.role);
        return selectedRole?.level_id === RoleLevels.PHARMACY && 
               (selectedRole?.name === 'Pharmacy Manager' || selectedRole?.name === 'Pharmacy Admin');
    }

    get iamBranchStaff() {
        const selectedRole = this.userRoles?.find(role => role.id === this.item.role);
        return selectedRole?.level_id === RoleLevels.BRANCH && 
               (selectedRole?.name === 'Branch Admin' || selectedRole?.name === 'Cashier' || selectedRole?.name === 'Reporting');
    }

    can(permission) {
        const store = useStore();
        const pL = JSON.parse(store.getters.gerUserPermissions);
        const f = pL.filter(e => e.name == permission);

        let res = false;

        if (f.length > 0) {
            res = true;
        }

        return res;
    }
}
